/**
 * Date Delivery Web Component
 * 
 * Affiche une date de livraison estimée avec effet shimmer et lazy loading
 * Compatible RGAA 4.1 avec support complet de l'accessibilité
 */

class DateDeliveryComponent extends HTMLElement {
    constructor() {
        super();
        this.isRendered = false;
        this.observer = null;
        this.shimmerObserver = null;
    }

    connectedCallback() {
        if (!this.isRendered) {
            this.setupIntersectionObserver();
        }
        this.setupShimmerObserver();
    }

    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        if (this.shimmerObserver) {
            this.shimmerObserver.disconnect();
            this.shimmerObserver = null;
        }
    }

    /**
     * Détecte quand le morph DOM restaure la classe shimmer sur un élément déjà rendu
     * (cas typique : mise à jour du panier via AJAX avec morphing de section)
     */
    setupShimmerObserver() {
        if (this.shimmerObserver) return;
        this.shimmerObserver = new MutationObserver(() => {
            if (this.classList.contains('c-date-deliv--shimmer') && this.isRendered) {
                this.isRendered = false;
                this.renderDeliveryDate();
            }
        });
        this.shimmerObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
    }

    // Observer les changements d'attributs
    static get observedAttributes() {
        return ['data-locale', 'data-country', 'data-days', 'data-format'];
    }

    attributeChangedCallback() {
        if (this.isRendered) {
            this.isRendered = false;
            this.renderDeliveryDate();
        }
    }
        
    
    /**
     * Configuration de l'Intersection Observer pour le lazy loading
     * @returns {void}
     */
    setupIntersectionObserver() {
        if (!('IntersectionObserver' in window)) {
            // Fallback pour les navigateurs non supportés
            this.renderDeliveryDate();
            return;
        }
        
        const options = {
            root: null,
            rootMargin: '50px', // Déclencher 50px avant que l'élément soit visible
            threshold: 0
        };
        
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !this.isRendered) {
                    this.renderDeliveryDate();
                    this.observer.disconnect();
                    this.observer = null;
                }
            });
        }, options);
        
        this.observer.observe(this);
    }
    
    // Calculer la date de livraison en ajoutant les jours ouvrés
    /**
     * Calcule la date de livraison en ajoutant un nombre de jours ouvrés à la date actuelle.
     * @param {number} businessDays - Nombre de jours ouvrés à ajouter.
     * @returns {Date} La date de livraison estimée.
     */
    calculateDeliveryDate(businessDays) {
        const today = new Date();
        let currentDate = new Date(today);
        let daysAdded = 0;

        // Validation de l'entrée
        if (typeof businessDays !== 'number' || isNaN(businessDays) || businessDays < 0) {
            throw new Error('Le paramètre businessDays doit être un nombre positif.');
        }

        // Boucle pour ajouter les jours ouvrés
        while (daysAdded < businessDays) {
            currentDate.setDate(currentDate.getDate() + 1);

            // Ignorer les week-ends (0 = Dimanche, 6 = Samedi)
            const dayOfWeek = currentDate.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                daysAdded++;
            }
        }

        return currentDate;
    }
 
    /**
     * Formater la date selon la locale et le format
     * @param {Date} date - La date à formater
     * @param {string} locale - La locale pour le formatage (ex: 'fr', 'en', 'en-US')
     * @param {'long'|'short'} format - Le format d'affichage ('long' ou 'short')
     * @returns {string} La date formatée selon la locale et le format
     */
    formatDate(date, locale, format = 'long') {
        try {
            // Vérifier si Temporal est disponible (futur standard)
            if (typeof Temporal !== 'undefined' && Temporal.PlainDate) {
                const plainDate = Temporal.PlainDate.from({
                    year: date.getFullYear(),
                    month: date.getMonth() + 1,
                    day: date.getDate()
                });
                
                if (format === 'short') {
                    return plainDate.toLocaleString(locale, {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric'
                    });
                } else {
                    return plainDate.toLocaleString(locale, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    });
                }
            }
            
            // Utiliser l'API Intl standard
            if (format === 'short') {
                return new Intl.DateTimeFormat(locale, {
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric'
                }).format(date);
            } else {
                return new Intl.DateTimeFormat(locale, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }).format(date);
            }
            
        } catch (error) {
            console.error('Erreur de formatage de date:', error);
            // Fallback vers le formatage par défaut
            return format === 'short' ? date.toLocaleDateString(locale) : date.toLocaleDateString(locale, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        }
    }
    
    /**
     * Rendu de la date de livraison avec gestion d'erreur
     * @returns {void}
     */
    renderDeliveryDate() {
        // Éviter le double rendu
        if (this.isRendered) {
            return;
        }
        
        try {
            // Récupérer les attributs
            const locale = this.getAttribute('data-locale') ?? 'en';
            const country = this.getAttribute('data-country') ?? 'US';
            const days = parseInt(this.getAttribute('data-days') ?? '7');
            const format = this.getAttribute('data-format') ?? 'long';
            
            // Validation - ne rien afficher en cas d'erreur
            if (days <= 0) {
                this.classList.remove('c-date-deliv--shimmer');
                this.setAttribute('aria-busy', 'false');
                this.innerHTML = '';
                this.isRendered = true;
                return;
            }
            
            // Calculer la date de livraison
            const deliveryDate = this.calculateDeliveryDate(days);
            
            // Formater la date selon la locale et le format
            const formattedDate = this.formatDate(deliveryDate, locale, format);
            
            // Retirer l'effet shimmer et mettre à jour l'accessibilité
            this.classList.remove('c-date-deliv--shimmer');
            this.setAttribute('aria-busy', 'false');
            
            // Afficher la date avec une description accessible
            const currentAriaLabel = this.getAttribute('aria-label') ?? 'Date de livraison';
            
            const span = document.createElement('span');
            span.className = 'c-date-deliv__text';
            span.setAttribute('aria-label', `${currentAriaLabel}: ${formattedDate}`);
            span.textContent = formattedDate;
            
            this.innerHTML = '';
            this.appendChild(span);
            
            this.isRendered = true;
            
        } catch (error) {
            console.error('Erreur dans DateDeliveryComponent:', error);
            this.classList.remove('c-date-deliv--shimmer');
            this.setAttribute('aria-busy', 'false');
            this.innerHTML = '';
            this.isRendered = true;
        }
    }
}

// Enregistrer le web component
customElements.define('date-delivery', DateDeliveryComponent);
